import TrustedHtml from '@components/common/TrustedHtml/TrustedHtml'
import Button from '@components/ui/Button'
import CheckboxV2 from '@components/ui/CheckboxV2/CheckboxV2'
import Container from '@components/ui/Container/Container'
import Form from '@components/ui/Form/Form'
import Input from '@components/ui/Input/Input'
import LineClamp from '@components/ui/LineClamp/LineClamp'
import Select from '@components/ui/Select/Select'
import { selectCountry, selectLabels } from '@redux/globalSlice'
import { isMobile } from '@utils/helpers/DeviceUtils'
import { pushNewsletterTrackingEvent } from '@utils/tracking-utils'
import clsx from 'clsx'
import Cookies from 'js-cookie'
import { useRouter } from 'next/router'

import { SendGridMailingList } from 'pages/api/email-sign-up'
import { FC, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import s from './ContactForm.module.scss'

type ContactFormProps = {
  headline: string
  successfulSubmissionText: string
  introductoryText: string
  enableIntroductoryText: boolean
  enableNameField: boolean

  termsText: string
  termsReadMoreText: string
  newsletterText: string
  sendEmailCopyText: string

  contactEntities?: ContactEntity[]
}

interface ContactEntity {
  contactName: string
  contactEmail: string
}

export type ContactFormData = {
  email: string
  phoneNumber: string
  name: string
  comment: string
  subject: string
  orderRef: string
  internalEmail: string
  newsletterSignup: Boolean
  sendEmailCopy: Boolean
  newsletterData: {
    list: SendGridMailingList
  }
}

const ContactFormComponent: FC<ContactFormProps> = ({
  headline,
  successfulSubmissionText,
  introductoryText,
  enableIntroductoryText,
  enableNameField,
  contactEntities,
  newsletterText,
  sendEmailCopyText,
  termsText,
  termsReadMoreText,
}) => {
  const labels = useSelector(selectLabels)

  const {
    labelContactFormEmail: labelEmail,
    labelContactFormFullName: labelFullName,
    labelContactFormSubject: labelSubject,
    labelContactFormComment: labelComment,
    labelContactFormSubmitButton: labelSubmit,
    labelContactFormOrderReference: labelOrderRef,
    labelContactFormPhoneNumber: labelPhoneNumber,
    labelContactFormChooseDepartment: labelChooseDepartment,
    labelContactFormChooseNewsletterList: labelChooseNewsletterList,
    labelContactFormPartner: labelPartner,
    labelContactFormProfessional: labelProfessional,
    labelContactFormConsumer: labelConsumer,
    errorRequiredField,
    errorEmailMalformed,
    errorPhoneNumberMalformed,
  } = useMemo(() => labels, [labels])

  // Test Commit

  const router = useRouter()

  const country = useSelector(selectCountry)

  const [newsletterChecked, setNewsletterChecked] = useState(false)
  const [mobile, setMobile] = useState<boolean>(false)

  useEffect(() => {
    setMobile(isMobile())
  }, [setMobile])

  return (
    <Container>
      {!contactEntities && (
        <p className="text-red mb-4">
          Error: Component misconfigured 'Contact entities' missing.
        </p>
      )}

      <div>
        <Form<ContactFormData>
          onSubmit={async (data) => {
            const urls = window.dataLayer.filter(
              (x: any) => x.event == 'pageview',
            )
            const lastUrl =
              urls.length > 1 ? urls[urls.length - 2].pagePath : ''
            const url = urls[urls.length - 1].pagePath

            await fetch(
              'https://hook.eu1.make.com/zu5br66698vn7w7jxazibs411qfcicpu',
              {
                method: 'POST',
                headers: {
                  'content-type': 'application/json',
                },
                body: JSON.stringify({
                  ...data,
                  newsletterData: {
                    list: data.newsletterData?.list,
                    language: router.query.language as string,
                    market: country?.code,
                    geoCountry: Cookies.get('homeCountry') ?? '',
                    url: url,
                    urlLastClick: lastUrl,
                    source: 'contact form gubi',
                  },
                }),
              },
            )

            if (data.newsletterSignup) {
              pushNewsletterTrackingEvent({
                newsletter: data.newsletterData.list,
                email: data.email,
              })
            }
          }}
        >
          {({
            register,
            getFieldState,
            formState: { touchedFields, isSubmitSuccessful, errors },
            setValue,
            getValues,
          }) => (
            <div>
              {!isSubmitSuccessful && (
                <div className={clsx('md:max-w-none')}>
                  <div className="mb-4">
                    <h2 className={'headerOne mb-2'}>{headline}</h2>
                    {enableIntroductoryText && <p>{introductoryText}</p>}
                  </div>
                  <div className="sm:flex sm:gap-10">
                    <div className={clsx('w-full sm:w-[450px]', s.inputGroup)}>
                      {enableNameField && (
                        <Input
                          title={labelFullName}
                          placeholder={labelFullName}
                          {...register('name', {
                            required: false,
                          })}
                        />
                      )}
                      <Input
                        type="email"
                        title={labelEmail}
                        placeholder={labelEmail}
                        {...register('email', {
                          required: errorRequiredField,
                          pattern: {
                            value: /\S+@\S+\.\S+/,
                            message: errorEmailMalformed,
                          },
                        })}
                      />
                      {errors && errors.email && (
                        <p className={clsx(s.error, 'text-red')}>
                          {errors.email.message}
                        </p>
                      )}
                      <Input
                        type="tel"
                        title={labelPhoneNumber}
                        placeholder={labelPhoneNumber}
                        {...register('phoneNumber', {
                          required: false,
                          pattern: {
                            value:
                              /(\+|)((?:9[679]|8[035789]|6[789]|5[90]|42|3[578]|2[1-689])|9[0-58]|8[1246]|6[0-6]|5[1-8]|4[013-9]|3[0-469]|2[70]|7|1)(?:\W*\d){0,13}\d$/,
                            message: errorPhoneNumberMalformed,
                          },
                        })}
                      />
                      {errors && errors.phoneNumber && (
                        <p className={clsx(s.error, 'text-red')}>
                          {errors.phoneNumber.message}
                        </p>
                      )}

                      <Select
                        title={labelChooseDepartment}
                        className={s.select}
                        {...register('internalEmail', {
                          required: errorRequiredField,
                        })}
                        required
                      >
                        <option value="" selected disabled>
                          {labelChooseDepartment}
                        </option>

                        {contactEntities &&
                          contactEntities.map((contactEntity, index) => {
                            return (
                              <option
                                value={contactEntity.contactEmail}
                                key={index}
                              >
                                {contactEntity.contactName}
                              </option>
                            )
                          })}
                      </Select>
                      {errors && errors.internalEmail && (
                        <p className={clsx(s.error, 'text-red')}>
                          {errors.internalEmail.message}
                        </p>
                      )}

                      {mobile && renderCommentArea(register, errors)}

                      <div>
                        <div className="mt-8 flex flex-col">
                          <div
                            className={clsx({ ['mb-4']: !newsletterChecked })}
                          >
                            <CheckboxV2
                              {...register(`newsletterSignup`, {
                                onChange: (e) => {
                                  setNewsletterChecked(e.target.checked)
                                },
                              })}
                              required={false}
                            >
                              <TrustedHtml html={newsletterText} />
                            </CheckboxV2>
                          </div>

                          {newsletterChecked && (
                            <div className="mb-4 w-full">
                              <Select
                                title={labelChooseNewsletterList}
                                className={s.select}
                                {...register('newsletterData.list', {
                                  required: errorRequiredField,
                                })}
                                required
                              >
                                <option value="" disabled selected>
                                  {labelChooseNewsletterList}
                                </option>
                                <option value="CONSUMERS">
                                  {labelConsumer}
                                </option>
                                <option value="PARTNERS">{labelPartner}</option>
                                <option value="PROFESSIONALS">
                                  {labelProfessional}
                                </option>
                              </Select>

                              {errors && errors.newsletterData && (
                                <p className={clsx(s.error, 'text-red')}>
                                  {errors.newsletterData.list.message}
                                </p>
                              )}
                            </div>
                          )}

                          <div className="mb-4">
                            <CheckboxV2
                              {...register(`sendEmailCopy`)}
                              required={false}
                            >
                              <TrustedHtml html={sendEmailCopyText} />
                            </CheckboxV2>
                          </div>

                          {termsText && (
                            <LineClamp
                              text={termsText}
                              readMoreText={termsReadMoreText}
                              textClassname={s.terms}
                            />
                          )}

                          <Button className="mt-3 w-full" variant="primary">
                            {labelSubmit}
                          </Button>
                        </div>
                      </div>
                    </div>
                    {!mobile && renderCommentArea(register, errors)}
                  </div>
                </div>
              )}

              {isSubmitSuccessful && (
                <div>
                  <TrustedHtml
                    html={
                      successfulSubmissionText ??
                      'Error: Succesful Submission Text is missing'
                    }
                  />
                </div>
              )}
            </div>
          )}
        </Form>
      </div>
    </Container>
  )

  function renderCommentArea(register, errors) {
    return (
      <div className={clsx('w-full', s.inputGroup)}>
        <Input
          title={labelSubject}
          className=""
          placeholder={labelSubject}
          {...register('subject', {
            required: errorRequiredField,
          })}
        />
        {errors && errors.subject && (
          <p className={clsx(s.error, 'text-red')}>{errors.subject.message}</p>
        )}

        <Input
          title={labelOrderRef}
          className=""
          placeholder={labelOrderRef}
          {...register('orderRef', {
            required: false,
          })}
        />

        <textarea
          title={labelComment}
          placeholder={labelComment}
          className={'w-full mt-4'}
          {...register('comment', {
            required: errorRequiredField,
          })}
        ></textarea>

        {errors && errors.comment && (
          <p className={clsx(s.error, 'text-red')}>{errors.comment.message}</p>
        )}
      </div>
    )
  }
}

export default ContactFormComponent
